import type { QueryResult, ResultRow } from '@idas/shared';

/**
 * Deciding what a result set *is*, before deciding how to draw it.
 *
 * A saved query is arbitrary SQL — nobody told us whether it returns one
 * number, a ranking, or a time series. Rather than make the analyst pick a
 * chart type per tile, the shape of the result picks the form, following the
 * usual rule that the data's job selects the visual: a lone value is a figure
 * to read, a label-plus-measure is a comparison of magnitude, a date-plus-
 * measure is a trend.
 *
 * When the shape is genuinely ambiguous the answer is a table, not a chart
 * drawn on a guess. A wrong chart is worse than no chart: it reads as a
 * finding.
 */

export type TileForm =
  | { kind: 'stat'; label: string; value: number; masked: boolean }
  | { kind: 'kpi'; items: Array<{ label: string; value: number; masked: boolean }> }
  | { kind: 'bar'; points: Point[]; measure: string; truncatedTo: number | null }
  | { kind: 'line'; points: Point[]; measure: string }
  | { kind: 'table' }
  | { kind: 'empty' };

export interface Point {
  label: string;
  value: number;
  /** Original cell, kept for the tooltip so dates read as the engine gave them. */
  raw: string;
}

/** Bars past this stop being comparable in a tile-sized chart. */
const MAX_BARS = 12;

/** Engine types we are willing to read as a number. */
const NUMERIC = /int|decimal|numeric|float|double|real|bit|year|count/i;
const TEMPORAL = /date|time|timestamp|year/i;

function isNumericColumn(result: QueryResult, index: number): boolean {
  const column = result.columns[index];
  if (!column) return false;
  // Trust the engine's type first; fall back to the values, because an
  // aggregate like COUNT(*) can arrive as a string over some drivers.
  if (NUMERIC.test(column.dataType)) return true;
  const sample = result.rows.slice(0, 20).map((r) => r[index]);
  const usable = sample.filter((v) => v !== null && v !== '');
  return usable.length > 0 && usable.every((v) => typeof v === 'number' || !Number.isNaN(Number(v)));
}

function isTemporalColumn(result: QueryResult, index: number): boolean {
  const column = result.columns[index];
  if (!column) return false;
  if (TEMPORAL.test(column.dataType)) return true;
  const sample = result.rows.slice(0, 8).map((r) => r[index]).filter((v) => v !== null);
  if (!sample.length) return false;
  return sample.every((v) => typeof v === 'string' && !Number.isNaN(Date.parse(v)));
}

function toNumber(cell: ResultRow[number] | undefined): number | null {
  if (cell === null || cell === undefined || cell === '') return null;
  const n = typeof cell === 'number' ? cell : Number(cell);
  return Number.isFinite(n) ? n : null;
}

function toLabel(cell: ResultRow[number] | undefined): string {
  if (cell === null || cell === undefined) return '—';
  if (typeof cell === 'string') return cell;
  return String(cell);
}

export function pickForm(result: QueryResult): TileForm {
  if (!result.rows.length || !result.columns.length) return { kind: 'empty' };

  const numeric = result.columns.map((_, i) => isNumericColumn(result, i));
  const numericCount = numeric.filter(Boolean).length;

  // One row, one number: a figure to read, not a one-bar bar chart.
  if (result.rows.length === 1 && numericCount === 1) {
    const index = numeric.indexOf(true);
    const value = toNumber(result.rows[0]![index]);
    if (value !== null) {
      return {
        kind: 'stat',
        label: result.columns[index]!.name,
        value,
        masked: result.columns[index]!.masked,
      };
    }
  }

  // A handful of headline numbers on one row is a KPI row, not a table and
  // certainly not a grouped bar: there is nothing to compare along, each
  // number is its own answer.
  if (result.rows.length === 1 && numericCount >= 2 && numericCount <= 4 && result.columns.length <= 4) {
    const items: Array<{ label: string; value: number; masked: boolean }> = [];
    result.columns.forEach((column, i) => {
      if (!numeric[i]) return;
      const value = toNumber(result.rows[0]![i]);
      if (value === null) return;
      items.push({ label: column.name, value, masked: column.masked });
    });
    if (items.length >= 2) return { kind: 'kpi', items };
  }

  // Exactly one measure alongside one descriptive column is the common shape
  // of a saved "count by X" query.
  if (result.columns.length >= 2 && numericCount === 1) {
    const valueIndex = numeric.indexOf(true);
    const labelIndex = numeric.indexOf(false);
    if (labelIndex !== -1) {
      const points: Point[] = [];
      for (const row of result.rows) {
        const value = toNumber(row[valueIndex]);
        if (value === null) continue;
        points.push({ label: toLabel(row[labelIndex]), value, raw: toLabel(row[labelIndex]) });
      }
      if (points.length >= 2) {
        const measure = result.columns[valueIndex]!.name;

        // A date on the x-axis means trend, and trend means a line — the
        // reader is following a shape, not comparing neighbours.
        if (isTemporalColumn(result, labelIndex) && points.length >= 3) {
          return { kind: 'line', points, measure };
        }

        // Ranked, then cut. Showing the top slice of an ordered result is
        // honest; showing an arbitrary 12 of 400 is not, so the tile says so.
        const ranked = [...points].sort((a, b) => b.value - a.value);
        const shown = ranked.slice(0, MAX_BARS);
        return {
          kind: 'bar',
          points: shown,
          measure,
          truncatedTo: ranked.length > MAX_BARS ? ranked.length : null,
        };
      }
    }
  }

  return { kind: 'table' };
}

/** Compact enough for a tile, exact enough to trust. */
export function formatNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  if (abs >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (abs >= 10_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** Full precision, for tooltips and the hero figure's title attribute. */
export function formatExact(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

export function formatAxisDate(raw: string): string {
  const t = Date.parse(raw);
  if (Number.isNaN(t)) return raw;
  return new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
